import axios, { AxiosError } from "axios";
import { instanceToPlain, plainToInstance } from "class-transformer";
import { ConsentsDto, } from "healthex-common/consent.dtos";
import { PatientConsentsByStudyDto, StudyPatientConsentTotalsResult, } from "healthex-common/patient.consent.dtos";
import { CheckPatientsExistsInput, } from "healthex-common/recruitment.dtos";
import { err, voidResult } from "healthex-common/result";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
import { i18nGetCurrentLanguage } from "./i18next";
import { TefcaProvider } from "healthex-common/features.dtos";
import { asEnum } from "healthex-common/utils";
export class PatientService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async requestEnrollmentConsent(studyId, studyOrgId, requestConsentInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/studies/:studyId/request-enrollment-consent`, {
            studyOrgId,
            studyId,
        }), requestConsentInput, await authenticatedApiConfig(this.authenticationProvider));
    }
    async updateHealthRecords(studyId, studyOrgId, requestConsentInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/studies/:studyId/$update-health-records`, {
            studyOrgId,
            studyId,
        }), requestConsentInput, await authenticatedApiConfig(this.authenticationProvider));
    }
    async revokeAllPatientConsents(patientId, revokeAllConsentsInput) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/$revokeAll`, {
            patientId,
        }), instanceToPlain(revokeAllConsentsInput), await authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientStudyConsent(patientId, studyId, proofOfConsentPdf) {
        const url = generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/:studyId`, {
            patientId,
            studyId,
        });
        const response = await axios.get(url, await authenticatedApiConfig(this.authenticationProvider, { proofOfConsentPdf }));
        return response.data;
    }
    async getFaq() {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patient-faq`, {}));
        return response.data;
    }
    async postSearchForStudyConsents(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/search`, {
            patientId,
        }), input, await authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(ConsentsDto, response.data.results) };
    }
    async postSearchForStudyPendingConsents(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/pending-consents/study/search`, {
            patientId,
        }), input, await authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(PatientConsentsByStudyDto, response.data.results) };
    }
    async postSearchForPatientStudyConsents(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/patient-consents/study/search`, {
            patientId,
        }), input, await authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return { total: response.data.total, results: plainToInstance(PatientConsentsByStudyDto, response.data.results) };
    }
    async postSearchForPatientStudyConsentsTotals(patientId, input, start, end) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/patient-consents/study/search/totals`, {
            patientId,
        }), input, await authenticatedApiConfig(this.authenticationProvider, { start, end }));
        return plainToInstance(StudyPatientConsentTotalsResult, response.data);
    }
    async updateStudyConsent(patientId, studyId, studyVersionId, consentType, consentUpdate) {
        consentUpdate.language = i18nGetCurrentLanguage();
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/:studyId/:studyVersionId/:consentType`, {
            patientId,
            studyId,
            studyVersionId,
            consentType,
        }), instanceToPlain(consentUpdate), await authenticatedApiConfig(this.authenticationProvider));
    }
    async updateStudyConsentBatch(patientId, studyId, studyVersionId, consentUpdateBatch) {
        consentUpdateBatch.update.language = i18nGetCurrentLanguage();
        await axios.patch(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/study/:studyId/:studyVersionId`, {
            patientId,
            studyId,
            studyVersionId,
        }), instanceToPlain(consentUpdateBatch), await authenticatedApiConfig(this.authenticationProvider));
    }
    async updateIASConsent(patientId, consentType, consentUpdate) {
        consentUpdate.language = i18nGetCurrentLanguage();
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consents/ias/:consentType`, {
            patientId,
            consentType,
        }), instanceToPlain(consentUpdate), await authenticatedApiConfig(this.authenticationProvider));
    }
    async getPatientPreferences(patientId, includeContactInfo = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/preferences`, {
            patientId,
        }), {
            ...(await authenticatedApiConfig(this.authenticationProvider)),
            params: includeContactInfo ? { includeContactInfo: "true" } : undefined,
        });
        return response.data === "" ? null : response.data;
    }
    async updatePatientPreferences(patientId, preferencesUpdate) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/preferences`, {
            patientId,
        }), preferencesUpdate, await authenticatedApiConfig(this.authenticationProvider));
    }
    async setPrimaryEmail(patientId, emailId) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/preferences/primary-email/:emailId`, {
            patientId,
            emailId,
        }), {}, await authenticatedApiConfig(this.authenticationProvider));
    }
    async setPrimaryPhone(patientId, phoneId) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/preferences/primary-phone/:phoneId`, {
            patientId,
            phoneId,
        }), {}, await authenticatedApiConfig(this.authenticationProvider));
    }
    async hasPatientConsentedToStudy(patientId, studyId, consentType, includeConsentRecord = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consented/study/:studyId/:consentType`, {
            patientId,
            studyId,
            consentType,
        }), {
            ...(await authenticatedApiConfig(this.authenticationProvider)),
            params: {
                ...(includeConsentRecord && { includeConsentRecord: true }),
            },
        });
        return response.data;
    }
    async hasPatientConsentedToIAS(patientId, consentType, includeConsentRecord = false) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consented/ias/:consentType`, {
            patientId,
            consentType,
        }), {
            ...(await authenticatedApiConfig(this.authenticationProvider)),
            params: {
                ...(includeConsentRecord && { includeConsentRecord: true }),
            },
        });
        return response.data;
    }
    async getConsentFlowProgress(patientId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consent-flow-progress`, { patientId }), input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateConsentFlowProgress(patientId, update) {
        var _a, _b, _c;
        try {
            await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consent-flow-progress`, { patientId }), update, await authenticatedApiConfig(this.authenticationProvider));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async markConsentFlowProgressResumed(patientId, input) {
        await axios.post(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consent-flow-progress/resumed`, { patientId }), input, await authenticatedApiConfig(this.authenticationProvider));
    }
    async deleteConsentFlowProgress(patientId, input) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/consent-flow-progress`, { patientId }), {
            ...(await authenticatedApiConfig(this.authenticationProvider)),
            data: input,
        });
    }
    async updateConsentFlowProgressByEmail(update) {
        await axios.put(generateURL(this.environment.API_BASE_URL, `/consent-flow-progress`), instanceToPlain(update));
    }
    async addPatients(projectId, input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/v1/projects/:projectId/patients`, {
            projectId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async checkForDuplicates(projectId, studyOrgId, patients) {
        if (patients.length === 0) {
            return [];
        }
        const input = new CheckPatientsExistsInput({
            patients: patients.map((p) => ({ email: p.email, phone: p.phone, externalId: p.externalId })),
        });
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:studyOrgId/projects/:projectId/patients/exists`, {
            studyOrgId,
            projectId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        const result = response.data;
        return result.results;
    }
    async previewNotification(projectId, organizationId, request) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, `/organizations/:organizationId/projects/:projectId/patients-notification-preview`, {
            organizationId,
            projectId,
        }), request, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async deleteAccount(patientId, input) {
        await axios.delete(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/delete-account`, {
            patientId,
        }), { ...(await authenticatedApiConfig(this.authenticationProvider)), data: input });
    }
    async updatePatient(patientId, input) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/updatePatient`, {
            patientId,
        }), input, await authenticatedApiConfig(this.authenticationProvider));
        return response.status === 200;
    }
    async getPatientAvatarUploadUrl(patientId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/avatarUploadUrl`, {
            patientId,
        }), localFileInfo, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getPatientTefcaProvider(email, organizationId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/tefca-provider`), await authenticatedApiConfig(this.authenticationProvider, { email, organizationId }));
        const provider = asEnum(TefcaProvider)(response.data);
        if (!provider) {
            throw new Error(`Invalid TEFCA provider: ${response.data}`);
        }
        return provider;
    }
    async getDefaultTefcaProvider() {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/tefca-provider/default`), await authenticatedApiConfig(this.authenticationProvider));
        const provider = asEnum(TefcaProvider)(response.data);
        if (!provider) {
            throw new Error(`Invalid TEFCA provider: ${response.data}`);
        }
        return provider;
    }
    async getPatientExternalId(patientId, studyId) {
        var _a, _b;
        try {
            const baseConfig = await authenticatedApiConfig(this.authenticationProvider);
            const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/patients/:patientId/external-id`, {
                patientId,
            }), { ...baseConfig, params: { studyId } });
            return (_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.externalId) !== null && _b !== void 0 ? _b : null;
        }
        catch {
            return null;
        }
    }
}
